/**
 * folioDeMinuta — qué zona del acta es el número de minuta.
 *
 * El folio no se escribe: lo reparte el servidor al guardar, consecutivo dentro
 * del año (`0012/2026`). Esta pantalla solo tiene que saber en qué casilla de
 * la plantilla va, para enseñarlo ahí y no dejar que se toque.
 *
 * Por qué no se puede editar. Un folio escrito a mano se repite —dos personas
 * abren su acta el mismo día y las dos ponen «13»— y se salta números cuando
 * alguien borra una minuta. En cuanto eso pasa una vez, el libro deja de servir
 * para lo único que sirve: encontrar un acta por su número. La casilla se ve,
 * pero la llena el sistema.
 *
 * El reconocimiento va por la etiqueta porque las zonas se deducen de la
 * plantilla de cada cliente: no hay un identificador estable que marcar. Se
 * exigen las dos cosas a la vez —que hable de minuta/acta y que hable de
 * número— para no confundirlo con «Número de asistentes» ni con «Acta anterior».
 */

const HABLA_DE_ACTA = /(folio|minuta|acta|sesi[oó]n)/i;
const HABLA_DE_NUMERO = /(folio|n[úu]m|\bno\.?\b|#|consecutivo|clave)/i;

/** ¿Esta etiqueta de la plantilla es el número de minuta? */
export function esEtiquetaDeFolio(etiqueta) {
  const texto = String(etiqueta || '').trim();
  if (!texto) return false;
  return HABLA_DE_ACTA.test(texto) && HABLA_DE_NUMERO.test(texto);
}

/**
 * La zona del folio, si la plantilla tiene una.
 *
 * Solo cuenta como folio una zona de dato suelto: una tabla titulada «Actas
 * anteriores» habla de actas y de números y no es el folio de esta.
 */
export function zonaDeFolio(regiones) {
  if (!Array.isArray(regiones)) return null;
  return regiones.find(r => r && r.tipo === 'valor' && esEtiquetaDeFolio(r.etiqueta)) || null;
}

/**
 * Los valores del acta con el folio puesto donde toca.
 *
 * Devuelve el mismo objeto cuando no hay nada que cambiar. Eso importa: arriba
 * se compara por identidad para decidir si hay cambios sin guardar, y devolver
 * una copia nueva en cada pintado dispararía un guardado detrás de otro.
 */
export function conFolio(valores, regiones, folio) {
  if (!folio) return valores;
  const zona = zonaDeFolio(regiones);
  if (!zona) return valores;
  if (valores && valores[zona.id] === folio) return valores;
  return { ...(valores || {}), [zona.id]: folio };
}
